import React, { useState, useEffect } from 'react';
import { styled } from 'linaria/react';

// import t from '@jetshop/intl';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import BrandLink from './components/BrandLink/BrandLink';
import { useCustomFields } from './components/useCustomFields';
import Gallery from './components/Gallery/Gallery';
import { SVG } from '../../assets/SVG';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import AddToCartForm from './components/AddToCart/AddToCartForm';
//import ProductMisc from './components/ProductMisc/ProductMisc';
import ProductInfo from './components/ProductInfo/ProductInfo';
import { theme } from '../Theme';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import BrandProducts from './components/ProductRecommendations/BrandProducts.js';
import SimilarProducts from './components/ProductRecommendations/SimilarProducts';
import OrderForm from './components/AddToCart/OrderForm';
import ShowVariantsLoggedOut from './components/ShowVariantsLoggedOut/ShowVariantsLoggedOut';
import { FamilyMembers } from './components/FamilyMembers/FamilyMembers';
import { usePriceDisplay } from '../../hooks/usePriceDisplay';
import { Link } from 'react-router-dom';
import t from '@jetshop/intl';
import CustomBadges from '../ui/CustomBadges';

const OuterWrapper = styled('div')`
  background: white;
  display: block;
  position: relative;
  width: 100%;
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  background: #f8f7f5;
  padding: 10px 20px;
  margin: 0 !important;
  &.borstiq {
    background: #ffffff;
  }
  * {
    text-decoration: none;
  }
  ${theme.below.lg} {
    padding: 5px 10px;
  }
`;

const ProductUpper = styled('div')`
  width: 100%;
  background: #f8f7f5;
  display: flex;
  justify-content: space-evenly;
  align-items: flex-start;
  grid-gap: 10px;

  &.borstiq {
    background: #ffffff;
  }

  ${theme.below.lg} {
    display: block;
    padding: 0;
  }
`;

const ProductImages = styled('div')`
  flex: 1;
  min-width: 100px;
  background: ${theme.colors.white};
  position: relative;
`;

const ProductBuyArea = styled('div')`
  flex: 2;
  width: 100%;
  max-width: 450px;

  &.borstiq {
    .button-select {
      .secondary {
        background: ${theme.colors.variant.accent} !important;
        color: ${theme.colors.variant.accentTextColor} !important;
      }
      button:active,
      button:hover {
        background: ${theme.colors.accent} !important;
        color: ${theme.colors.accentTextColor} !important;
      }
    }

    .cta:hover {
      > div {
        color: #282828 !important;
      }
      border: none;
    }

    .qty-adjuster {
      button,
      input {
        background: ${theme.colors.variant.accent};
      }
    }
  }

  .inner {
    flex: 1;
    padding: 20px;
    border-radius: 3px;
    .product-overtitles {
      display: flex;
      span,
      a {
        color: #282828;
        margin-right: 3px;
        text-decoration: none;
        text-transform: uppercase;
        font-size: 26px;
        font-weight: 700;
        letter-spacing: 1.4px;
      }
    }
    .product-titles {
      margin-bottom: 20px;
      ${theme.below.lg} {
        margin-bottom: 10px;
      }
      h1 {
        font-family: Averta Std;
        font-size: 26px;
        font-weight: 500;
        text-transform: uppercase;
        font-weight: 700;
        line-height: 1;
        color: #282828;
        margin-top: -10px;
        margin-bottom: 3px;
        ${theme.below.lg} {
          font-size: 22px;
        }
      }
      h2 {
        color: #282828;
        font-size: 14px;
        margin: 0;
        line-height: 1;
      }
    }
    .product-articlenumber {
      font-size: 14px;
    }
    .product-usp {
      margin-bottom: 20px;
      ${theme.below.lg} {
        margin-bottom: 10px;
      }
      .product-usp-item {
        display: flex;
        align-items: center;
        margin: 0;
        .icon {
          margin-right: 5px;
        }
        .text {
          font-size: 12px;
          line-height: 1;
        }
      }
    }

    .product-description {
      font-size: 14px;
      line-height: 150%;
      margin-bottom: 10px;
      margin-top: 2.2rem;
    }
    .product-specifications {
      margin-top: 2.2rem;

      .specifications-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        padding: 1rem 0 0.5rem 0;
        border-top: 1px solid #e9e5df;
      }
      .spec-title {
        font-weight: 700;
        text-transform: uppercase;
        font-size: 13px;
        line-height: 1;
        color: #282828;
        margin: 0;
      }
      .spec-value {
        font-size: 14px;
        line-height: 1;
        color: #282828;
        margin: 0;
      }
    }
  }
`;

const ArticleNumbersWrapper = styled('div')`
  display: flex;
  justify-content: flex-start;
  div {
    color: #282828;
    font-size: 12px;
    font-weight: 700;
    line-height: 139.5%; /* 16.74px */
    letter-spacing: 0.48px;
    text-transform: uppercase;
    margin-right: 20px;
  }
  span {
    font-size: 14px;
    font-weight: 400;
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;

  text-transform: none;
  margin-bottom: 20px;
  padding: 4px 30px 0;
  cursor: pointer;
  background: #282828;
  height: 40px;
  color: #fff;
  text-decoration: none;
`;

export const checkForLongDescription = (description, maxLength) => {
  const isLongDescription = description?.length > maxLength;
  const productDescription = isLongDescription
    ? description?.substring(0, maxLength) + '...'
    : description;

  return {
    productDescription: productDescription,
    isLongDescription: isLongDescription
  };
};

const Product = ({ result: { data, loading }, product, partialProduct }) => {
  // KEEP OPEN TABS AND ACCORDION STATE
  const [remoteOpenIndex, setRemoteOpenIndex] = useState(0);
  // GET INITIAL VARIANT FROM URL
  const initialVariant = useVariantFromUrl();
  // DEFINE VARIANTHANDLER TO USE PRODUCTVARIANTS
  const variantHandler = useProductVariants(product, {
    initialVariant,
    preselectCheapest: true
  });
  // GET SELECTED VARIANT
  const {
    selectedVariant: selectedVariation,
    getMissingOptions
  } = variantHandler;

  // GET PARENTS FOR BREADCRUMBS
  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);

  // GET DATA FROM CUSTOMFIELDS
  const { USP1, USP2, USP3, Varumarke, Variants } = useCustomFields(product);

  // FILTER OUT EMPTY USP
  const productUsps = [USP1?.value, USP2?.value, USP3?.value].filter(
    usp => usp
  );

  const colorVariants = product?.customFields?.find(
    ({ key }) => key === 'Variants'
  )?.listValues;

  // SCROLL TO HASH IF AVAILABLE
  useEffect(() => {
    const hash = sessionStorage.getItem('targetHash');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          sessionStorage.removeItem('targetHash');
        }
      }, 200); // Adjust the timeout as needed
    }
  }, []);

  const excludedCustomFields = ['files', 'Variants', 'vimeoId'];
  const filteredCustomFields = product?.customFields?.filter(
    customField => !excludedCustomFields.includes(customField.key)
  );
  const specifications = filteredCustomFields;

  const isBorstiq =
    process.env.REACT_APP_LOGO_NAME === 'Borstiq' ? true : false;

  return (
    <OuterWrapper>
      <StyledBreadcrumbs
        breadcrumbs={data?.route?.breadcrumbs || []}
        parents={parents}
        className={isBorstiq && 'borstiq'}
      />
      <ProductUpper className={isBorstiq && 'borstiq'}>
        <ProductImages>
          <Gallery product={product} />
          {product?.badges && (
            <CustomBadges
              badges={product.badges}
              placement="bottom"
              useImageHeight={product?.images?.length > 2}
            />
          )}
        </ProductImages>

        <ProductBuyArea className={isBorstiq && 'borstiq'}>
          <div className="inner">
            <div className="product-overtitles">
              {Varumarke?.value || product?.subName ? (
                <BrandLink brand={Varumarke?.value || product?.subName} />
              ) : null}
            </div>
            <div className="product-titles">
              <h1>{product?.mainHeader || product?.name}</h1>
            </div>

            {colorVariants?.length > 0 && (
              <FamilyMembers
                articleNumbers={colorVariants}
                title={'FINNS ÄVEN SOM'}
                current={product}
                customFieldKey={'RelateradeProdukter'}
                variant={selectedVariation}
              />
            )}

            <div
              className="product-description"
              dangerouslySetInnerHTML={{
                __html: product?.shortDescription
              }}
            />

            {/* PRODUCT USP FROM CUSTOM FIELDS */}
            {productUsps && (
              <div className="product-usp">
                {productUsps?.map((usp, index) => (
                  <div key={index} className="product-usp-item">
                    <span className="icon">
                      <SVG name="UspCheck" />
                    </span>
                    <span className="text">{usp}</span>
                  </div>
                ))}
              </div>
            )}
            {product && (
              <AddToCartForm
                product={product}
                variant={selectedVariation}
                getMissingOptions={getMissingOptions}
                variantHandler={variantHandler}
              />
            )}

            {/* <div
              className="product-description"
              dangerouslySetInnerHTML={{
                __html: product?.shortDescription
              }}
            /> */}
            <div className="product-specifications">
              {specifications?.map((customField, index) => {
                return (
                  <div key={index} className="specifications-row">
                    <p className="spec-title">{customField?.title}</p>
                    <p className="spec-value">{customField?.stringValue}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </ProductBuyArea>
      </ProductUpper>

      {/* PRODUCT INFO, TABS DESKTOP, ACCORDION MOBILE */}
      <ProductInfo
        product={product}
        remoteOpenIndex={remoteOpenIndex}
        setRemoteOpenIndex={setRemoteOpenIndex}
      />

      {/* {(Varumarke?.value || product?.subName) && (
        <BrandProducts
          brand={Varumarke?.value || product?.subName}
          product={product}
        />
      )} */}
      {product?.canonicalCategory?.id && <SimilarProducts product={product} />}
    </OuterWrapper>
  );
};

export default Product;
